import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllItems } from '../services/api';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  const slugify = (text) =>
    (text || '')
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const normalizePhotoValue = (value) => {
    if (!value || typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (trimmed.startsWith('data:image')) return trimmed;
    if (trimmed.startsWith('image/upload/https://') || trimmed.startsWith('image/upload/http://')) {
      return trimmed.replace(/^image\/upload\//, '');
    }
    return trimmed;
  };

  useEffect(() => {
    let cancelled = false;

    const loadNewestHeroItems = async () => {
      try {
        setLoading(true);
        const res = await getAllItems();
        const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);

        const mapped = items
          .map((item) => {
            const id = item?.id;
            const name = item?.name || '';
            const category = item?.category || '';
            const rawPrice = parseFloat(item?.price);
            const priceNumber = Number.isNaN(rawPrice) ? 0 : rawPrice;

            let image = '';
            const candidateSingleRaw = item?.photo || item?.image;
            const candidateSingle = normalizePhotoValue(candidateSingleRaw);
            const candidateArrayRaw = item?.images || [];
            const candidateArray = Array.isArray(candidateArrayRaw)
              ? candidateArrayRaw
                  .map((img) => {
                    if (!img) return '';
                    if (typeof img === 'string') return normalizePhotoValue(img);
                    if (typeof img === 'object' && typeof img.image === 'string') return normalizePhotoValue(img.image);
                    return '';
                  })
                  .filter(Boolean)
              : [];

            if (candidateSingle && candidateSingle.trim() !== '') {
              image = candidateSingle;
            } else if (candidateArray.length > 0) {
              const thumbObj = Array.isArray(candidateArrayRaw)
                ? candidateArrayRaw.find((x) => x && typeof x === 'object' && x.is_thumbnail)
                : null;
              if (thumbObj && typeof thumbObj.image === 'string') {
                image = normalizePhotoValue(thumbObj.image);
              } else {
                image = candidateArray[0] || '';
              }
            }

            return { id, name, category, priceNumber, image };
          })
          .filter((x) => x?.id && x?.name && x?.image);

        const newest = [...mapped]
          .sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0))
          .slice(0, 4);

        if (cancelled) return;

        if (newest.length === 0) {
          setSlides([]);
          return;
        }

        const heroSlides = newest.map((p) => ({
          image: p.image,
          title: (p.category || 'FURNITURE').toString().toUpperCase(),
          subtitle: p.name.toString().toUpperCase(),
          price: `KSh ${Number(p.priceNumber || 0).toLocaleString('en-KE', { maximumFractionDigits: 0 })}`,
          buttonText: 'Shop Now',
          link: `/product/${encodeURIComponent(p.id)}/${encodeURIComponent(slugify(p.name))}`,
        }));

        setSlides(heroSlides);
        setCurrentSlide(0);
      } catch (_) {
        if (!cancelled) {
          setSlides([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadNewestHeroItems();
    return () => {
      cancelled = true;
    };
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Auto-advance slides every 5 minutes
  useEffect(() => {
    if (slides.length <= 1) return;
    const intervalId = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000); // 5 seconds

    return () => clearInterval(intervalId);
  }, [slides.length]);

  return (
    <div className="relative m-3 sm:m-6">
      {/* Carousel Container */}
      <div className="relative h-[400px] sm:h-[500px] lg:h-[600px] overflow-hidden rounded-2xl sm:rounded-3xl shadow-2xl">
        {loading && slides.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="text-gray-700 font-semibold">Loading...</div>
          </div>
        )}
        {!loading && slides.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="text-gray-700 font-semibold">No items available</div>
          </div>
        )}
        {/* Slides */}
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-transform duration-500 ease-in-out ${
              index === currentSlide ? 'translate-x-0' : 
              index < currentSlide ? '-translate-x-full' : 'translate-x-full'
            }`}
          >
            {/* Background Image with Overlay */}
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat rounded-3xl"
              style={{
                backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('${slide.image}')`
              }}
            />
            
            {/* Decorative angled edge */}
            <div className="absolute top-8 right-8 w-20 h-20 bg-white transform rotate-45 opacity-15 rounded-lg"></div>
            
            {/* Content Overlay */}
            <div className="relative z-10 h-full flex items-center justify-center">
              <div className="text-center text-white px-4 sm:px-6 max-w-2xl">
                {/* Title - Animate from top */}
                <h2 className={`text-sm sm:text-lg font-medium mb-2 sm:mb-4 tracking-wider transform transition-all duration-1000 ease-out ${
                  index === currentSlide ? 'translate-y-0 opacity-100' : '-translate-y-20 opacity-0'
                }`}>
                  {slide.title}
                </h2>
                
                {/* Subtitle - Animate from bottom */}
                <h1 className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 sm:mb-6 tracking-wide leading-tight transform transition-all duration-1000 ease-out delay-200 ${
                  index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
                }`}>
                  <span className="bg-gradient-to-r from-orange-400 via-yellow-300 to-white bg-clip-text text-transparent drop-shadow-sm">
                    {slide.subtitle}
                  </span>
                </h1>
                
                {/* Price - Split animation from left and right */}
                <div className={`text-lg sm:text-xl mb-6 sm:mb-8 font-light transform transition-all duration-1000 ease-out delay-400 ${
                  index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}>
                  <span className={`inline-block transform transition-all duration-800 ease-out delay-500 ${
                    index === currentSlide ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
                  }`}>
                    Start From
                  </span>
                  {' '}
                  <span className={`inline-block font-semibold transform transition-all duration-800 ease-out delay-600 ${
                    index === currentSlide ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
                  }`}>
                    {slide.price}
                  </span>
                </div>
                
                {/* Button - Animate from bottom */}
                <button
                  type="button"
                  onClick={() => navigate(slide.link || '/shop')}
                  className={`bg-orange-500 hover:bg-orange-600 text-white px-6 sm:px-8 py-3 rounded-md font-semibold transition-all duration-1000 ease-out delay-700 text-base sm:text-lg transform touch-manipulation ${
                    index === currentSlide ? 'translate-y-0 opacity-100 hover:scale-105' : 'translate-y-20 opacity-0'
                  }`}
                >
                  {slide.buttonText}
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Navigation Arrows */}
        {slides.length > 1 && (
          <>
            <button 
              onClick={prevSlide}
              className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 sm:p-3 rounded-full transition-colors z-20 touch-manipulation"
              aria-label="Previous"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button 
              onClick={nextSlide}
              className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 sm:p-3 rounded-full transition-colors z-20 touch-manipulation"
              aria-label="Next"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        {/* Dots Indicator */}
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'bg-orange-500 w-8' : 'bg-white/60 hover:bg-white/80'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
